<script lang="ts">
  import confetti from 'canvas-confetti';
  import type { DisplayView, PublicPlayer } from '@banger/shared';
  import { send } from '../lib/connection';
  import Avatar from '../shared/Avatar.svelte';

  let { view, byId, onnewroom }: {
    view: DisplayView; byId: Map<string, PublicPlayer>; onnewroom: () => void;
  } = $props();

  const ranked = $derived((view.ranking ?? view.players.map((p) => p.id)).flatMap((id) => byId.get(id) ?? []));
  const champ = $derived(ranked[0]);
  // Podium order on screen: 2nd, 1st, 3rd.
  const podium = $derived(
    [ranked[1], ranked[0], ranked[2]].flatMap((p, i) => (p ? [{ p, place: [2, 1, 3][i]! }] : [])),
  );
  const rest = $derived(ranked.slice(3));
  const album = $derived(view.mode === 'album');

  const reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const COLORS = ['#ff3df2', '#7df9ff', '#9775fa', '#ffffff', '#ff8ef7'];

  $effect(() => {
    if (reduced) return;
    const fire = () => {
      const o = { colors: COLORS, disableForReducedMotion: true, zIndex: 200, particleCount: 70, spread: 75, startVelocity: 60 };
      confetti({ ...o, angle: 60, origin: { x: 0, y: 0.75 } });
      confetti({ ...o, angle: 120, origin: { x: 1, y: 0.75 } });
    };
    const big = setTimeout(() => confetti({ colors: COLORS, disableForReducedMotion: true, zIndex: 200, particleCount: 220, spread: 140, origin: { x: 0.5, y: 0.45 }, scalar: 1.3 }), 300);
    const loop = setInterval(fire, 1100);
    return () => {
      clearTimeout(big);
      clearInterval(loop);
      confetti.reset();
    };
  });

  let again = $state(false);
  function playAgain() {
    again = true;
    send({ type: 'play_again' });
    setTimeout(() => (again = false), 3000);
  }
</script>

<main>
  <p class="kicker">{album ? 'ALBUM CHAMPION' : 'BANGER CHAMPION'}</p>
  {#if champ}
    <h1 class="champ"><span class="crown">👑</span> {champ.emoji} {champ.name}</h1>
  {/if}

  <div class="podium">
    {#each podium as { p, place } (p.id)}
      <div class="step p{place}">
        <Avatar emoji={p.emoji} size={place === 1 ? 120 : 88} />
        <span class="name">{p.name}</span>
        {#if album}
          <span class="pts"><b>{p.points ?? 0}</b> pts</span>
          <span class="stats">{p.correctTotal} correct · {p.bangers} track{p.bangers === 1 ? '' : 's'} won</span>
        {:else}
          <span class="stats"><b>{p.bangers}</b> BANGER{p.bangers === 1 ? '' : 's'} · {p.correctTotal} correct</span>
        {/if}
        <div class="block"><span>{place}</span></div>
      </div>
    {/each}
  </div>

  {#if rest.length}
    <ol class="rest" start="4">
      {#each rest as p (p.id)}
        <li>{p.emoji} {p.name} <span>· {album ? `${p.points ?? 0} pts` : `${p.bangers} BANGER${p.bangers === 1 ? '' : 's'}`} · {p.correctTotal} correct</span></li>
      {/each}
    </ol>
  {/if}

  <footer>
    <button class="btn-neon big" disabled={again} onclick={playAgain}>Play again</button>
    <button class="ghost" onclick={onnewroom}>New room</button>
  </footer>
</main>

<style>
  main { min-height: 100%; display: grid; align-content: center; justify-items: center; gap: 2vh; padding: 4vh 4vw; text-align: center; }
  .kicker {
    margin: 0; font-size: clamp(2rem, 4.4vw, 4rem); font-weight: 900; letter-spacing: 0.14em; color: #fff;
    text-shadow: 0 0 8px var(--pink), 0 0 24px var(--pink), 0 0 60px var(--pink);
    animation: flicker 6s infinite;
  }
  .champ { margin: 0; font-size: clamp(3rem, 7vw, 6.5rem); font-weight: 900; line-height: 1.05; text-shadow: 0 0 14px var(--cyan), 0 0 40px var(--cyan); animation: zoom 0.8s cubic-bezier(0.2, 1.6, 0.4, 1) both; }
  .crown { display: inline-block; animation: bob 1.4s ease-in-out infinite alternate; }
  .podium { display: flex; align-items: flex-end; justify-content: center; gap: 2vw; }
  .step { display: grid; justify-items: center; gap: 0.8vh; width: clamp(150px, 18vw, 280px); animation: up 0.7s ease-out both; }
  .step.p1 { animation-delay: 0.6s; }
  .step.p2 { animation-delay: 0.3s; }
  .name { font-size: clamp(1.4rem, 2.6vw, 2.4rem); font-weight: 900; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .stats { font-size: clamp(0.95rem, 1.5vw, 1.3rem); opacity: 0.8; }
  .stats b { color: var(--pink); }
  .pts { font-size: clamp(1.3rem, 2.4vw, 2.2rem); font-weight: 800; color: var(--cyan); }
  .pts b { font-size: 1.3em; font-variant-numeric: tabular-nums; color: #fff; text-shadow: 0 0 10px var(--pink), 0 0 26px var(--pink); }
  .block {
    /* Compact steps keep Play again on a 720p screen (Album podiums also carry a points line). */
    width: 100%; display: flex; align-items: flex-start; justify-content: center; padding-top: 0.6vh;
    border-radius: 14px 14px 0 0; font-size: clamp(1.8rem, 3.4vw, 3.4rem); font-weight: 900;
    background: linear-gradient(rgba(125, 249, 255, 0.35), rgba(58, 10, 94, 0.4));
    box-shadow: 0 0 24px rgba(125, 249, 255, 0.35), inset 0 0 0 2px rgba(125, 249, 255, 0.6);
  }
  .p1 .block { height: 14vh; background: linear-gradient(rgba(255, 61, 242, 0.6), rgba(58, 10, 94, 0.4)); box-shadow: 0 0 40px rgba(255, 61, 242, 0.6), inset 0 0 0 2px var(--pink); }
  .p2 .block { height: 10.5vh; }
  .p3 .block { height: 8vh; }
  .rest { margin: 0; padding: 0; list-style-position: inside; font-size: clamp(1.1rem, 2vw, 1.7rem); font-weight: 700; display: flex; flex-wrap: wrap; gap: 0.5vh 2.4vw; justify-content: center; }
  .rest span { opacity: 0.65; font-weight: 500; }
  footer { display: flex; gap: 2vw; align-items: center; margin-top: 1vh; }
  .big { font-size: clamp(1.6rem, 3vw, 2.6rem); padding: 0.4em 1.8em; }
  .ghost {
    font: inherit; font-weight: 800; font-size: clamp(1.2rem, 2.2vw, 1.9rem); color: #fff; cursor: pointer;
    background: transparent; border: 2px solid rgba(125, 249, 255, 0.6); border-radius: 999px; padding: 0.4em 1.4em;
  }
  .ghost:hover { background: rgba(125, 249, 255, 0.12); }
  @keyframes zoom { from { transform: scale(0.3); opacity: 0; } to { transform: none; opacity: 1; } }
  @keyframes up { from { transform: translateY(80px); opacity: 0; } to { transform: none; opacity: 1; } }
  @keyframes bob { to { transform: translateY(-10px) rotate(-8deg); } }
</style>
